import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { MapPin, Loader2, Search, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useLocations, sel } from "@/components/stock/StockActions";
import { LocationTypePill } from "@/components/stock/StockBadges";
import { Pager } from "@/pages/stock/StockOverviewPage";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatApiErrorDetail } from "@/lib/api";

export default function StockByLocationPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user?.is_super || user?.permissions?.includes("stock.manage");
  const [locations, reloadLocations] = useLocations();
  const [locId, setLocId] = useState("");
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1, page: 1 });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [nm, setNm] = useState(""); const [sub, setSub] = useState(""); const [ty, setTy] = useState("warehouse");

  useEffect(() => { if (!locId && locations.length) setLocId(locations[0].location_id); }, [locations, locId]);
  useEffect(() => {
    if (!locId) return;
    setLoading(true);
    api.get("/stock/by-location", { params: { location_id: locId, q: q || undefined, page } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [locId, q, page]);

  const createLoc = async () => {
    if (!nm.trim()) { toast.error("Name required"); return; }
    try { await api.post("/stock/locations", { name: nm, subtitle: sub, type: ty }); toast.success("Location created"); setAddOpen(false); setNm(""); setSub(""); reloadLocations(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const current = locations.find((l) => l.location_id === locId);

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="stock-by-location">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3"><PageIcon />
          <div><h1 className="text-2xl font-bold text-slate-900">Stock by Location</h1><p className="text-sm text-slate-500">Balances held at warehouses, showrooms and vehicles.</p></div>
        </div>
        {canManage && <Button variant="outline" className="gap-1.5" onClick={() => setAddOpen(true)} data-testid="add-location-btn"><Plus className="w-4 h-4" /> New Location</Button>}
      </div>

      <div className="mb-4 max-w-sm" data-testid="location-select-wrap">
        <label className="text-xs font-semibold text-slate-500">Location</label>
        <select value={locId} onChange={(e) => { setPage(1); setLocId(e.target.value); }} className={sel} data-testid="location-select">
          {locations.map((l) => <option key={l.location_id} value={l.location_id}>{`${l.name}${l.subtitle ? ` — ${l.subtitle}` : ""} · ${l.total_held ?? 0} held`}</option>)}
        </select>
      </div>

      {current && (
        <div className="flex items-center gap-3 mb-4"><LocationTypePill type={current.type} />
          {current.subtitle && <span className="text-sm text-slate-600 font-medium">{current.subtitle}</span>}
          <span className="text-sm text-slate-500">{current.product_lines || 0} product lines · {current.total_held || 0} units held · {current.total_allocated || 0} allocated</span>
        </div>
      )}

      <div className="relative mb-4 max-w-md"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search product at this location…" className="pl-9" data-testid="by-location-search" /></div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 bg-slate-50/70 border-b border-slate-100">
            <th className="py-2.5 px-4">Product</th>
            <th className="py-2.5 px-2 text-right">Held</th><th className="py-2.5 px-2 text-right">Reserved</th>
            <th className="py-2.5 px-2 text-right">Allocated</th><th className="py-2.5 px-2 text-right">Available</th>
            <th className="py-2.5 px-2 text-right">On Order</th><th className="py-2.5 px-4 text-right">Last Count</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-50">
            {loading ? <tr><td colSpan={7} className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
            : data.items.length === 0 ? <tr><td colSpan={7} className="py-10 text-center text-slate-400">No stock at this location.</td></tr>
            : data.items.map((r) => (
              <tr key={r.product_legacy_id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/products/${r.product_legacy_id}`)} data-testid={`byloc-row-${r.product_legacy_id}`}>
                <td className="py-2.5 px-4"><div className="font-medium text-slate-800 truncate max-w-[300px]">{r.title}</div><div className="text-xs text-slate-400 font-mono">{r.product_legacy_id}</div></td>
                <td className="py-2.5 px-2 text-right tabular-nums">{r.qty_held}</td>
                <td className="py-2.5 px-2 text-right tabular-nums text-slate-500">{r.qty_reserved}</td>
                <td className="py-2.5 px-2 text-right tabular-nums text-amber-600">{r.qty_allocated}</td>
                <td className="py-2.5 px-2 text-right tabular-nums font-semibold text-emerald-600">{r.qty_available}</td>
                <td className="py-2.5 px-2 text-right tabular-nums text-slate-500">{r.qty_on_order}</td>
                <td className="py-2.5 px-4 text-right text-xs text-slate-400">{r.last_stocktake_date ? new Date(r.last_stocktake_date).toLocaleDateString("en-GB") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={data.page} totalPages={data.total_pages} total={data.total} onPage={setPage} />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="modal-add-location">
          <DialogHeader><DialogTitle>New Stock Location</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><span className="text-xs text-slate-500">Name</span><Input value={nm} onChange={(e) => setNm(e.target.value)} data-testid="new-location-name" /></div>
            <div><span className="text-xs text-slate-500">Subtitle</span><Input value={sub} onChange={(e) => setSub(e.target.value)} placeholder="e.g. Unit 4, Trade Park" data-testid="new-location-subtitle" /></div>
            <div><span className="text-xs text-slate-500">Type</span>
              <select className={sel} value={ty} onChange={(e) => setTy(e.target.value)} data-testid="new-location-type">
                {["warehouse", "showroom", "van", "engineer_vehicle", "depot", "other"].map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button><Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={createLoc} data-testid="new-location-save">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
